import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
	createRoutesFromChildren,
	matchRoutes,
	RouterProvider,
	useLocation,
	useNavigationType,
} from 'react-router';
import './styles/index.css';
import * as Sentry from '@sentry/react';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { Agentation } from 'agentation';
import { NavermapsProvider } from 'react-naver-maps';
import Router from '@/app/App';
import AppToaster from '@/app/providers/AppToaster';
import { DAY, SECOND } from '@/shared/consts';

Sentry.init({
	dsn: 'https://d0907c8d9af9bcb646737fe6014d424f@o4510415556706304.ingest.us.sentry.io/4511269054316544',
	// Setting this option to true will send default PII data to Sentry.
	// For example, automatic IP address collection on events
	sendDefaultPii: true,
	environment: import.meta.env.MODE,
	integrations: [
		Sentry.reactRouterV7BrowserTracingIntegration({
			useEffect: useEffect,
			useLocation,
			useNavigationType,
			createRoutesFromChildren,
			matchRoutes,
		}),
		Sentry.replayIntegration(),
	],
	// Tracing
	tracesSampleRate: 1.0, //  Capture 100% of the transactions
	// Session Replay
	replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
	replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
	// Enable logs to be sent to Sentry
	enableLogs: true,
});

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			gcTime: DAY,
			throwOnError: true,
		},
	},
});

const asyncStoragePersister = createAsyncStoragePersister({
	storage: window.localStorage,
	key: 'mantang-query-cache',
	throttleTime: SECOND,
});

// biome-ignore lint/style/noNonNullAssertion: root element always exists
createRoot(document.getElementById('root')!).render(
	<StrictMode>
		<PersistQueryClientProvider
			client={queryClient}
			persistOptions={{
				persister: asyncStoragePersister,
			}}
		>
			<NavermapsProvider
				ncpKeyId={import.meta.env.VITE_NAVER_MAP_CLIENT_ID}
				submodules={['geocoder']}
			>
				<RouterProvider router={Router} />
			</NavermapsProvider>
			<AppToaster />
			{process.env.NODE_ENV === 'development' && (
				<ReactQueryDevtools
					initialIsOpen={false}
					buttonPosition="bottom-left"
				/>
			)}
		</PersistQueryClientProvider>
		{process.env.NODE_ENV === 'development' && <Agentation />}
	</StrictMode>,
);
